/*
  Time helpers, split out of the utils barrel so modules that need only these
  can import them directly. Importing them through the barrel from another
  barrel member creates a require cycle, which Metro warns about.
*/
import { i18n } from "../i18n/init";
export const getTimeBreakdown = (timeDiffMs) => {
    /*
      Floored at zero, because `Math.floor` rounds a negative away from it: a diff
      a few seconds the wrong way breaks down to -1 of every unit, and the
      formatters below read straight off these numbers and say so out loud.
  
      A diff arrives negative whenever its two ends are read at different moments
      — a clock pinned at mount against a timestamp that moved after it, or a
      device clock a little behind the server that stamped one. Neither has a
      sensible breakdown, and both mean "no time at all" to every caller here.
    */
    const elapsedMs = Math.max(0, timeDiffMs);
    const days = Math.floor(elapsedMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor(elapsedMs / (1000 * 60 * 60));
    const minutes = Math.floor(elapsedMs / (1000 * 60));
    const seconds = Math.floor(elapsedMs / 1000);
    return { days, hours, minutes, seconds };
};
export const formatTimeNumber = (num) => {
    return num.toString().padStart(2, "0");
};
// ============ Time Until Target Helpers ============
/**
 * Get milliseconds until a specific UTC hour (today or tomorrow if already passed)
 */
export const getMsUntilUTCHour = (hour) => {
    const now = new Date();
    const target = new Date(now);
    target.setUTCHours(hour, 0, 0, 0);
    if (now.getTime() >= target.getTime()) {
        target.setUTCDate(target.getUTCDate() + 1);
    }
    return target.getTime() - now.getTime();
};
/**
 * Get milliseconds until a target Date (adjusts to tomorrow if passed)
 */
export const getMsUntilTarget = (targetTime) => {
    const now = new Date();
    const target = new Date(targetTime);
    if (now.getTime() >= target.getTime()) {
        target.setDate(target.getDate() + 1);
    }
    return target.getTime() - now.getTime();
};
// ============ Formatting Functions ============
/*
  Hermes (the mobile runtime) formats units wrongly: 4 hours comes back as
  "14,400 seconds" and 2 days as "2 day". Mobile is English-only, so it skips
  Intl and takes the plain form.
*/
const isHermes = "HermesInternal" in globalThis;
/**
 * Format time as "X days", "X hours", or "X minutes" in the UI locale
 * ("5 dage", "16 timer"), so the strings it gets interpolated into stay
 * translated.
 */
export const formatTime = (timeDiffMs) => {
    const { days, hours, minutes } = getTimeBreakdown(timeDiffMs);
    const [value, unit] = days >= 1
        ? [days, "day"]
        : hours >= 1
            ? [hours, "hour"]
            : [minutes, "minute"];
    if (isHermes)
        return `${value} ${unit}${value === 1 ? "" : "s"}`;
    try {
        const formatted = new Intl.NumberFormat(i18n.language || "en-US", {
            style: "unit",
            unit,
            unitDisplay: "long",
        }).format(value);
        // A runtime without unit formatting can hand back the bare number.
        if (formatted !== String(value))
            return formatted;
    }
    catch {
        // Fall through to the English form.
    }
    return `${value} ${unit}${value === 1 ? "" : "s"}`;
};
/** "2h ago", translated: the short form for tight rows. */
export const formatTimeAgoShort = (timeDiffMs) => {
    const { days, hours, minutes } = getTimeBreakdown(timeDiffMs);
    const [count, unit] = days >= 1
        ? [days, "day"]
        : hours >= 1
            ? [hours, "hour"]
            : [minutes, "minute"];
    return i18n.t(`common:agoShort.${unit}`, { count });
};
/*
  SBC sets that never really expire carry an end date years out (2035), and a
  countdown off one reads as a data bug — "Expires in: 3,265 days". Past this
  the expiry is dropped instead.
*/
const MAX_EXPIRY_DAYS = 60;
/** Formatted expiry, or "" when it is too far out to mean anything. */
export const formatExpiry = (timeDiffMs) => {
    const { days } = getTimeBreakdown(timeDiffMs);
    return days > MAX_EXPIRY_DAYS ? "" : formatTime(timeDiffMs);
};
/**
 * formatExpiry for an end time in unix seconds, which is how the API carries
 * it. "" for a set with no end time, so callers can branch on the one value.
 */
export const formatExpiryAt = (endTimeSeconds) => endTimeSeconds ? formatExpiry(endTimeSeconds * 1000 - Date.now()) : "";
/**
 * Format time as short "Xh" or "Xm" (hours if > 0, otherwise minutes)
 */
export const formatTimeShort = (timeDiffMs) => {
    const { hours, minutes } = getTimeBreakdown(timeDiffMs);
    if (hours > 0) {
        return `${hours}h`;
    }
    return `${minutes % 60}m`;
};
/**
 * Format time as "HH:MM:SS"
 */
export const formatTimeHMS = (timeDiffMs) => {
    const totalSeconds = Math.floor(timeDiffMs / 1000);
    const hours = Math.floor(totalSeconds / 3600) % 24;
    const minutes = Math.floor(totalSeconds / 60) % 60;
    const seconds = totalSeconds % 60;
    return `${formatTimeNumber(hours)}:${formatTimeNumber(minutes)}:${formatTimeNumber(seconds)}`;
};
/**
 * Format refresh interval description
 */
export const formatRefreshTime = (timeDiffMs) => {
    const { days, hours, minutes } = getTimeBreakdown(timeDiffMs);
    if (days === 7)
        return "weekly";
    if (hours === 24)
        return "daily";
    if (days >= 1) {
        return `every ${days} days`;
    }
    if (hours >= 1) {
        return `every ${hours} hours`;
    }
    return `every ${minutes} minutes`;
};
//# sourceMappingURL=time.js.map